/**
 * src/ui/condiciones-modal.js — modal "Responsabilidades y Condiciones de Uso".
 *
 * Se abre desde el botón homónimo de la card "Sistema de Puntos" (pacto.js),
 * debajo de "Ver reglamento completo". Texto legal, sobrio, sin emojis, pero
 * con el mismo lenguaje visual glass del resto. Sin auto-open ni flag: se abre
 * con el botón y se cierra por X / CTA / tap en el backdrop.
 *
 * Mismo patrón que reglamento-modal.js (header sticky + body scrolleable de
 * mini-cards + footer sticky). Clases con prefijo cnd- y CSS propio inyectado
 * una vez, para no acoplarse a los modales ya en producción.
 *
 * Copy FINAL aprobado por el dueño — NO reescribir.
 */

const MODAL_ID = 'condicionesModal';
const STYLE_ID = 'condiciones-modal-css';

const INTRO = 'PACTO MUNDIAL es una plataforma social y recreativa diseñada para vivir el Mundial de fútbol junto a amigos mediante predicciones deportivas, rankings y rituales futboleros.';

// Copy canónico — NO reescribir. 9 secciones, títulos en mayúscula, sin emojis.
const SECCIONES = [
    {
        titulo: 'NATURALEZA DEL SERVICIO',
        lineas: [
            'PACTO MUNDIAL no constituye una plataforma de apuestas, juegos de azar, inversiones ni actividades financieras.',
            'Todos los puntajes, rankings, rituales, reconocimientos y clasificaciones tienen carácter exclusivamente recreativo y de entretenimiento.',
        ],
    },
    {
        titulo: 'PACTOS, PREMIOS Y CASTIGOS',
        lineas: [
            'Los premios, castigos, aportes económicos, desafíos o compromisos definidos dentro de cada grupo son acuerdos privados entre sus integrantes.',
            'PACTO MUNDIAL no recauda, administra, custodia, garantiza ni distribuye dinero, bienes, premios o contraprestaciones acordadas entre jugadores.',
            'La plataforma no interviene en disputas, incumplimientos, desacuerdos o reclamos relacionados con dichos acuerdos.',
        ],
    },
    {
        titulo: 'EL PACTO CERTIFICADO',
        lineas: [
            'El pacto certificado representa un acuerdo social y recreativo entre los integrantes de un grupo.',
            'PACTO MUNDIAL solo informa lo acordado por el grupo.',
            'Al certificar el pacto, se habilita la visualización del ranking del grupo y la posibilidad de que el grupo suba sus rituales a la Tribuna Pública.',
            'La certificación del pacto tiene efectos exclusivamente dentro de la plataforma y no constituye un contrato legal exigible frente a PACTO MUNDIAL.',
        ],
    },
    {
        titulo: 'CONTENIDO GENERADO POR LOS USUARIOS',
        lineas: [
            'Las arengas, cábalas, promesas, titulares, comentarios y cualquier otro contenido publicado son responsabilidad exclusiva de sus autores.',
            'Cada usuario es responsable de las consecuencias derivadas de los contenidos que publique dentro de la plataforma.',
        ],
    },
    {
        titulo: 'MODERACIÓN COMUNITARIA',
        lineas: [
            'PACTO MUNDIAL promueve el folklore futbolero, la creatividad, el humor y la pasión por el Mundial.',
            'Los usuarios pueden señalar contenidos que consideren ofensivos, agresivos, discriminatorios, fraudulentos, ilegales o contrarios al espíritu de la comunidad mediante la opción: "AL PASTO"',
            'La plataforma podrá limitar, ocultar, despublicar o eliminar contenidos reportados, así como aplicar medidas adicionales cuando corresponda.',
            'Las decisiones de moderación son definitivas y podrán realizarse tanto mediante revisión automática como mediante revisión humana.',
        ],
    },
    {
        titulo: 'DISPONIBILIDAD DEL SERVICIO',
        lineas: [
            'PACTO MUNDIAL realiza esfuerzos razonables para mantener la disponibilidad y el correcto funcionamiento de la plataforma.',
            'Sin embargo, no garantiza disponibilidad ininterrumpida ni ausencia total de errores técnicos, caídas de servicio, problemas de conectividad o fallas de terceros.',
        ],
    },
    {
        titulo: 'INFORMACIÓN DEPORTIVA',
        lineas: [
            'Los resultados, posiciones, fixtures, estadísticas y demás datos deportivos provienen de servicios externos especializados.',
            'Ante eventuales errores, demoras, modificaciones o inconsistencias en dichas fuentes, prevalecerán los criterios de validación definidos por PACTO MUNDIAL para el cálculo de puntajes y rankings.',
        ],
    },
    {
        titulo: 'LIMITACIÓN DE RESPONSABILIDAD',
        lineas: [
            'PACTO MUNDIAL no actúa como árbitro, mediador ni garante en conflictos entre integrantes de grupos.',
            'La plataforma no asume responsabilidad por pérdidas económicas, incumplimientos de pactos, premios no entregados, castigos no cumplidos o cualquier acuerdo privado celebrado entre jugadores.',
        ],
    },
    {
        titulo: 'ACEPTACIÓN',
        lineas: [
            'El uso de PACTO MUNDIAL implica la aceptación del Reglamento General y de las presentes Condiciones de Uso.',
            'El objetivo de la plataforma es promover la amistad, la participación, el folklore futbolero y la experiencia colectiva del Mundial.',
        ],
    },
];

// HTML puro del modal — exportado para test.
export const buildCondicionesModalHTML = () => {
    const secciones = SECCIONES.map((s) => `
        <div class="cnd-section">
            <h3 class="cnd-section-title">${s.titulo}</h3>
            ${s.lineas.map((l) => `<p class="cnd-section-line">${l}</p>`).join('')}
        </div>
    `).join('');

    return `
        <div id="${MODAL_ID}" class="cnd-overlay" role="dialog" aria-modal="true" aria-label="Responsabilidades y Condiciones de Uso">
            <div class="cnd-card glass-card">
                <header class="cnd-header">
                    <h2 class="cnd-title">RESPONSABILIDADES Y CONDICIONES DE USO</h2>
                    <button class="cnd-close" type="button" aria-label="Cerrar">✕</button>
                </header>
                <div class="cnd-body">
                    <p class="cnd-intro">${INTRO}</p>
                    ${secciones}
                </div>
                <footer class="cnd-footer">
                    <button class="btn btn-primary cnd-cta" type="button">Entendido</button>
                </footer>
            </div>
        </div>
    `;
};

// CSS inyectado una sola vez (idempotente). Mismo lenguaje visual que rgl-/csj-.
const MODAL_CSS = `
    .cnd-overlay {
        position: fixed; inset: 0; z-index: 100;
        background: rgba(0,0,0,.68); backdrop-filter: blur(6px);
        display: flex; align-items: center; justify-content: center;
        padding: 1rem; animation: cndModalIn .18s ease-out;
    }
    @keyframes cndModalIn { from { opacity: 0; } to { opacity: 1; } }
    .cnd-card {
        position: relative; width: min(100%, 420px); max-height: calc(100dvh - 2rem);
        display: flex; flex-direction: column; padding: 0; overflow: hidden;
        border-radius: 16px;
        background: radial-gradient(circle at 14% 0%, rgba(117,170,219,.12), transparent 40%), rgba(12,18,30,.98);
        border: 1px solid rgba(117,170,219,.22);
    }
    .cnd-header {
        flex: 0 0 auto; display: flex; align-items: center; justify-content: space-between;
        gap: .6rem; padding: 1rem 1.1rem .8rem;
        border-bottom: 1px solid rgba(255,255,255,.07);
        background: rgba(12,18,30,.6);
    }
    .cnd-title {
        margin: 0; color: var(--white);
        font-family: var(--font-heading); font-size: .92rem;
        letter-spacing: .04em; line-height: 1.2; text-transform: uppercase;
    }
    .cnd-close {
        flex: 0 0 auto; width: 30px; height: 30px; border-radius: 999px;
        border: 1px solid rgba(255,255,255,.14); background: rgba(255,255,255,.05);
        color: var(--white); font-size: .85rem; cursor: pointer;
        display: inline-flex; align-items: center; justify-content: center;
    }
    .cnd-close:hover { background: rgba(255,255,255,.1); }
    .cnd-body {
        flex: 1 1 auto; overflow-y: auto; padding: .9rem 1.1rem;
        display: flex; flex-direction: column; gap: .6rem;
    }
    .cnd-intro {
        margin: 0 0 .2rem; color: rgba(226,232,240,.92);
        font-size: .84rem; line-height: 1.42;
    }
    .cnd-section {
        padding: .7rem .8rem; border-radius: 11px;
        background: rgba(255,255,255,.035); border: 1px solid rgba(255,255,255,.07);
    }
    .cnd-section-title {
        margin: 0 0 .4rem; color: var(--white);
        font-family: var(--font-heading); font-size: .82rem;
        font-weight: 900; letter-spacing: .04em; text-transform: uppercase;
    }
    .cnd-section-line {
        margin: 0 0 .3rem; color: var(--text-muted);
        font-size: .8rem; line-height: 1.42;
    }
    .cnd-section-line:last-child { margin-bottom: 0; }
    .cnd-footer {
        flex: 0 0 auto; padding: .8rem 1.1rem 1rem;
        border-top: 1px solid rgba(255,255,255,.07);
        background: rgba(12,18,30,.6);
    }
    .cnd-cta { width: 100%; margin: 0; }
`;

const injectCSS = () => {
    if (typeof document === 'undefined' || !document.head) return;
    try {
        if (!document.getElementById?.(STYLE_ID)) {
            const styleEl = document.createElement('style');
            styleEl.id = STYLE_ID;
            styleEl.textContent = MODAL_CSS;
            document.head.appendChild?.(styleEl);
        }
    } catch { /* polyfills mínimos de test no tienen head completo — no romper imports */ }
};
injectCSS();

const escListener = (e) => { if (e.key === 'Escape') closeCondicionesModal(); };

export const openCondicionesModal = () => {
    if (typeof document === 'undefined') return;
    closeCondicionesModal();   // idempotente
    injectCSS();               // defensivo si el import corrió sin head
    const container = document.createElement('div');
    container.innerHTML = buildCondicionesModalHTML();
    const modal = container.firstElementChild;
    if (!modal) return;
    document.body.appendChild(modal);
    modal.querySelector('.cnd-close')?.addEventListener('click', closeCondicionesModal);
    modal.querySelector('.cnd-cta')?.addEventListener('click', closeCondicionesModal);
    modal.addEventListener('click', (e) => { if (e.target === modal) closeCondicionesModal(); });
    document.addEventListener('keydown', escListener);
};

export const closeCondicionesModal = () => {
    if (typeof document === 'undefined') return;
    document.getElementById(MODAL_ID)?.remove();
    document.removeEventListener?.('keydown', escListener);
};

// Bridges para el onclick="" inline del botón en pacto.js.
if (typeof window !== 'undefined') {
    window.openCondicionesModal = openCondicionesModal;
    window.closeCondicionesModal = closeCondicionesModal;
}
